(() => {
  /*
   * ============================================================
   * HITOKI DEVICE EVENT - SEPTEMBER 2026
   *
   * Offer:
   * - $60 off qualifying Saber
   * - $100 off qualifying Trident
   * - Complimentary Hitoki Essentials Kit
   *
   * The monetary discounts are handled by Functions Creator.
   * This script ONLY manages the automatic Essentials Kit:
   *
   * 1. Add one kit when a qualifying device is in cart.
   * 2. Keep gift quantity locked at one.
   * 3. Remove gift when no qualifying device remains.
   * 4. Confirm the Functions discount actually made it free.
   * 5. Remove gift if it would otherwise cost the customer $59.98.
   *
   * Gift variant:
   * 53373638607137
   * ============================================================
   */

  const CONFIG = {
    giftVariantId: 53373638607137,

    qualifyingVariantIds: [
      // Existing individual Saber products
      49319515783457, // Saber Portable Kit
      49319605043489, // Saber Bubbler Kit
      49319354827041, // Saber Recycler Kit
      49319475118369, // Saber Solo

      // Combined Saber Kits product
      50910016209185, // Solo - Device Only
      50910016110881, // Bubbler Kit
      50910016143649, // Portable Kit
      50910016176417, // Recycler Kit

      // Trident
      51191641211169, // Trident V3
      51191686463777  // Signature Series Bundle
    ],

    campaignParameter: 'deviceevent',
    campaignStorageKey: 'hitoki_device_event_sep_2026',

    /*
     * Used for popup/email attribution and suppression.
     * It does NOT control gift eligibility.
     */
    campaignHours: 168
  };

  let syncing = false;
  let syncTimer = null;

  function root() {
    return window.Shopify?.routes?.root || '/';
  }

  /*
   * ------------------------------------------------------------
   * CAMPAIGN TRACKING
   * ------------------------------------------------------------
   *
   * Email and popup links can contain:
   *
   * ?deviceevent=1
   *
   * We retain that flag for 7 days.
   *
   * IMPORTANT:
   * The gift does NOT depend on this flag.
   * The Device Event is available publicly.
   */
  function captureCampaignSource() {
    try {
      const params = new URLSearchParams(window.location.search);

      if (params.get(CONFIG.campaignParameter) === '1') {
        const expires =
          Date.now() +
          CONFIG.campaignHours * 60 * 60 * 1000;

        localStorage.setItem(
          CONFIG.campaignStorageKey,
          JSON.stringify({
            active: true,
            expires: expires
          })
        );
      }
    } catch (error) {
      console.warn(
        'Hitoki Device Event: unable to store campaign source.',
        error
      );
    }
  }

  /*
   * Optional helper if we need this flag elsewhere later.
   */
  function campaignWasActivated() {
    try {
      const stored = JSON.parse(
        localStorage.getItem(CONFIG.campaignStorageKey)
      );

      if (!stored?.active || !stored?.expires) {
        return false;
      }

      if (Date.now() > stored.expires) {
        localStorage.removeItem(CONFIG.campaignStorageKey);
        return false;
      }

      return true;
    } catch {
      return false;
    }
  }

  /*
   * ------------------------------------------------------------
   * SHOPIFY CART HELPERS
   * ------------------------------------------------------------
   */

  async function getCart() {
    const response = await fetch(root() + 'cart.js', {
      credentials: 'same-origin'
    });

    if (!response.ok) {
      throw new Error('Unable to retrieve cart.');
    }

    return response.json();
  }

  async function addGift() {
    const response = await fetch(root() + 'cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      credentials: 'same-origin',

      body: JSON.stringify({
        items: [
          {
            id: CONFIG.giftVariantId,
            quantity: 1,

            /*
             * Private promotion identifier.
             * This is useful in Shopify order reporting.
             */
            properties: {
              '_promotion': 'Hitoki Device Event Sep 2026'
            }
          }
        ]
      })
    });

    const data = await response.json().catch(() => ({}));

    if (!response.ok) {
      throw new Error(
        data.description ||
        'Unable to add Hitoki Essentials Kit.'
      );
    }

    return data;
  }

  async function changeLine(lineKey, quantity) {
    const response = await fetch(root() + 'cart/change.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      credentials: 'same-origin',

      body: JSON.stringify({
        id: lineKey,
        quantity: quantity
      })
    });

    const data = await response.json().catch(() => ({}));

    if (!response.ok) {
      throw new Error(
        data.description ||
        'Unable to update Hitoki Essentials Kit.'
      );
    }

    return data;
  }

  /*
   * ------------------------------------------------------------
   * CART DETECTION
   * ------------------------------------------------------------
   */

  function hasQualifyingDevice(cart) {
    return cart.items.some(item =>
      CONFIG.qualifyingVariantIds.includes(
        Number(item.variant_id)
      )
    );
  }

  function getGiftLines(cart) {
    return cart.items.filter(
      item =>
        Number(item.variant_id) ===
        Number(CONFIG.giftVariantId)
    );
  }

  /*
   * ------------------------------------------------------------
   * CONCEPT CART REFRESH
   * ------------------------------------------------------------
   */

  function refreshConceptCart(open = false) {
    document.dispatchEvent(
      new CustomEvent('cart:refresh', {
        bubbles: true,
        detail: {
          open: open
        }
      })
    );
  }

  /*
   * ------------------------------------------------------------
   * FREE-GIFT SAFETY CHECK
   * ------------------------------------------------------------
   *
   * Functions Creator should make the Essentials Kit $0.
   *
   * We check several times because Shopify may briefly need
   * time to recalculate automatic discounts after cart/add.js.
   */

  async function verifyGiftIsFree() {
    const delays = [150, 350, 700];

    for (const delay of delays) {
      await new Promise(resolve =>
        setTimeout(resolve, delay)
      );

      const cart = await getCart();
      const gift = getGiftLines(cart)[0];

      if (!gift) {
        return false;
      }

      if (Number(gift.final_line_price) === 0) {
        return true;
      }
    }

    /*
     * Important fail-safe:
     *
     * Never leave the promotional kit in the cart at full price.
     */
    const cart = await getCart();
    const gift = getGiftLines(cart)[0];

    if (gift) {
      console.warn(
        'Hitoki Device Event: gift discount did not apply. Removing Essentials Kit.'
      );

      await changeLine(gift.key, 0);
    }

    return false;
  }

  /*
   * ------------------------------------------------------------
   * REMOVE GIFTS
   * ------------------------------------------------------------
   */

  async function removeAllGiftLines(cart) {
    let gifts = getGiftLines(cart);

    while (gifts.length) {
      await changeLine(gifts[0].key, 0);

      const updatedCart = await getCart();
      gifts = getGiftLines(updatedCart);
    }
  }

  /*
   * ------------------------------------------------------------
   * MAIN PROMOTION SYNC
   * ------------------------------------------------------------
   */

  async function syncDeviceEvent(openDrawer = false) {
    if (syncing) {
      return;
    }

    syncing = true;

    try {
      captureCampaignSource();

      let cart = await getCart();

      const qualifies = hasQualifyingDevice(cart);
      let gifts = getGiftLines(cart);

      /*
       * --------------------------------------------------------
       * QUALIFYING DEVICE PRESENT
       * --------------------------------------------------------
       */
      if (qualifies) {

        /*
         * Add the gift if it isn't already present.
         */
        if (gifts.length === 0) {
          await addGift();

          const isFree = await verifyGiftIsFree();

          if (isFree) {
            refreshConceptCart(true);
          }

          return;
        }

        /*
         * Force the first gift line to quantity 1.
         */
        if (gifts[0].quantity !== 1) {
          await changeLine(gifts[0].key, 1);

          cart = await getCart();
          gifts = getGiftLines(cart);
        }

        /*
         * Remove duplicate gift lines if they somehow appear.
         */
        while (gifts.length > 1) {
          await changeLine(gifts[1].key, 0);

          cart = await getCart();
          gifts = getGiftLines(cart);
        }

        /*
         * Verify the remaining gift is actually free.
         */
        const isFree = await verifyGiftIsFree();

        if (isFree) {
          refreshConceptCart(openDrawer);
        }

        return;
      }

      /*
       * --------------------------------------------------------
       * NO QUALIFYING DEVICE
       * --------------------------------------------------------
       *
       * The promotional gift should never remain by itself.
       */
      if (!qualifies && gifts.length) {
        await removeAllGiftLines(cart);

        refreshConceptCart(false);
      }

    } catch (error) {
      console.error(
        'Hitoki Device Event:',
        error
      );
    } finally {
      syncing = false;
    }
  }

  /*
   * ------------------------------------------------------------
   * DEBOUNCED SYNC
   * ------------------------------------------------------------
   */

  function scheduleSync(openDrawer = false) {
    clearTimeout(syncTimer);

    syncTimer = setTimeout(() => {
      syncDeviceEvent(openDrawer);
    }, 175);
  }

  /*
   * ------------------------------------------------------------
   * CONCEPT THEME EVENTS
   * ------------------------------------------------------------
   */

  document.addEventListener(
    'page:loaded',
    () => {
      scheduleSync(false);
    }
  );

  /*
   * Normal AJAX Add to Cart.
   */
  document.addEventListener(
    'ajaxProduct:added',
    () => {
      scheduleSync(true);
    }
  );

  /*
   * Quantity change / remove.
   */
  document.addEventListener(
    'cart:updated',
    () => {
      scheduleSync(false);
    }
  );

  /*
   * Browser restoration / back-forward navigation.
   */
  window.addEventListener(
    'pageshow',
    () => {
      scheduleSync(false);
    }
  );

  /*
   * Normal DOM fallback.
   */
  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      () => {
        scheduleSync(false);
      }
    );
  } else {
    scheduleSync(false);
  }

})();